/**
 * 术语候选审核面板（PM用）
 *
 * 功能：
 * 1. 显示客户端所有待审核术语候选（包括自动发现和译者提交）
 * 2. 区分自动发现（AI）和人工提交（译者）
 * 3. 支持单个/批量批准、拒绝
 * 4. 支持修改候选内容后批准
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { termsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { TableShell } from '@/components/layout/table-shell'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Check, X, Loader2, Bot, User, Pencil } from 'lucide-react'
import { toast } from 'sonner'
import { useState } from 'react'

interface TermCandidate {
  id: string
  projectId: string
  clientId: string
  sourceText: string
  targetText?: string  // 新版单语候选为空字符串；旧版双语候选 / 译者提交有值
  context?: string
  confidence?: number
  occurrenceCount?: number
  status: string
  suggestedBy?: string
  suggester?: {
    id: string
    name: string
  } | null
  reviewedBy?: string
  createdAt: number
}

interface TermCandidateReviewPanelProps {
  clientId: string
}

export function TermCandidateReviewPanel({ clientId }: TermCandidateReviewPanelProps) {
  const queryClient = useQueryClient()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [editingCandidate, setEditingCandidate] = useState<TermCandidate | null>(null)
  const [editForm, setEditForm] = useState({
    sourceText: '',
    targetText: '',
    context: '',
  })

  const { data, isLoading } = useQuery({
    queryKey: ['term-candidates-client', clientId, 'pending'],
    queryFn: () => termsApi.getCandidatesByClient(clientId, 'pending'),
    refetchInterval: 15000,
  })

  const candidates = (data as any)?.candidates || []

  const approveMutation = useMutation({
    mutationFn: (candidateId: string) => termsApi.approveCandidate(candidateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['term-candidates-client', clientId] })
      queryClient.invalidateQueries({ queryKey: ['terms', clientId] })
      toast.success('术语已批准')
    },
    onError: (error: any) => {
      toast.error(error.message || '批准失败')
    },
  })

  const rejectMutation = useMutation({
    mutationFn: (candidateId: string) => termsApi.rejectCandidate(candidateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['term-candidates-client', clientId] })
      toast.success('术语已拒绝')
    },
    onError: (error: any) => {
      toast.error(error.message || '拒绝失败')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => termsApi.updateCandidate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['term-candidates-client', clientId] })
      toast.success('术语已更新')
      setEditingCandidate(null)
    },
    onError: (error: any) => {
      toast.error(error.message || '更新失败')
    },
  })

  const batchApproveMutation = useMutation({
    mutationFn: (candidateIds: string[]) => termsApi.batchApproveCandidates(candidateIds),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['term-candidates-client', clientId] })
      queryClient.invalidateQueries({ queryKey: ['terms', clientId] })
      setSelectedIds(new Set())
      toast.success(`已批准 ${(data as any).approved} 个术语`)
    },
    onError: (error: any) => {
      toast.error(error.message || '批量批准失败')
    },
  })

  const batchRejectMutation = useMutation({
    mutationFn: (candidateIds: string[]) => termsApi.batchRejectCandidates(candidateIds),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['term-candidates-client', clientId] })
      setSelectedIds(new Set())
      toast.success(`已拒绝 ${(data as any).rejected} 个术语`)
    },
    onError: (error: any) => {
      toast.error(error.message || '批量拒绝失败')
    },
  })

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const selectAll = () => {
    if (selectedIds.size === candidates.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(candidates.map((c: TermCandidate) => c.id)))
    }
  }

  const openEdit = (candidate: TermCandidate) => {
    setEditingCandidate(candidate)
    setEditForm({
      sourceText: candidate.sourceText,
      targetText: candidate.targetText || '',
      context: candidate.context || '',
    })
  }

  const saveEdit = () => {
    if (!editingCandidate) return
    updateMutation.mutate({
      id: editingCandidate.id,
      data: {
        sourceText: editForm.sourceText,
        targetText: editForm.targetText,
        context: editForm.context,
      },
    })
  }

  const isAutoDiscovered = (candidate: TermCandidate) => !candidate.suggestedBy

  return (
    <div className="space-y-4">
      <TableShell
        title={`待审核术语 (${candidates.length})`}
        description="包括自动发现的术语和译者提交的术语建议"
        toolbar={
          selectedIds.size > 0 ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">
                已选择 {selectedIds.size} 项
              </span>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs bg-success/10 hover:bg-success/20 text-success-foreground border-success/30"
                onClick={() => batchApproveMutation.mutate(Array.from(selectedIds))}
                disabled={batchApproveMutation.isPending}
              >
                {batchApproveMutation.isPending ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Check className="h-3 w-3 mr-1" />
                )}
                批量批准
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs bg-destructive/10 hover:bg-destructive/20 text-destructive border-destructive/30"
                onClick={() => batchRejectMutation.mutate(Array.from(selectedIds))}
                disabled={batchRejectMutation.isPending}
              >
                {batchRejectMutation.isPending ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <X className="h-3 w-3 mr-1" />
                )}
                批量拒绝
              </Button>
            </div>
          ) : null
        }
      >
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[40px]">
                <input
                  type="checkbox"
                  checked={selectedIds.size === candidates.length && candidates.length > 0}
                  onChange={selectAll}
                  className="h-4 w-4 rounded border-gray-300"
                />
              </TableHead>
              <TableHead className="w-[100px]">来源</TableHead>
              <TableHead>源术语</TableHead>
              <TableHead>目标术语</TableHead>
              <TableHead className="w-[120px]">建议人</TableHead>
              <TableHead className="w-[80px]">置信度</TableHead>
              <TableHead className="w-[160px] text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  <p className="text-xs mt-2">加载中...</p>
                </TableCell>
              </TableRow>
            ) : candidates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center text-muted-foreground text-sm">
                  暂无待审核术语
                </TableCell>
              </TableRow>
            ) : (
              candidates.map((candidate: TermCandidate) => (
                <TableRow
                  key={candidate.id}
                  className={
                    selectedIds.has(candidate.id) ? 'bg-warning/10' : ''
                  }
                >
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(candidate.id)}
                      onChange={() => toggleSelect(candidate.id)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </TableCell>
                  <TableCell>
                    {isAutoDiscovered(candidate) ? (
                      <Badge variant="outline" className="text-[10px] h-5 gap-1">
                        <Bot className="w-3 h-3" />
                        AI发现
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] h-5 gap-1 bg-info/10 text-black border-info/30">
                        <User className="w-3 h-3" />
                        译者提交
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm font-medium">
                    {candidate.sourceText}
                  </TableCell>
                  <TableCell className="text-sm text-success-foreground">
                    {candidate.targetText ? (
                      candidate.targetText
                    ) : (
                      <Badge variant="outline" className="text-[10px] h-5 bg-muted/40 text-muted-foreground">
                        单语
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {candidate.suggester?.name || '-'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {candidate.confidence
                      ? `${(candidate.confidence * 100).toFixed(0)}%`
                      : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                        onClick={() => openEdit(candidate)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-success hover:text-success-foreground hover:bg-success/20"
                        onClick={() => approveMutation.mutate(candidate.id)}
                        disabled={approveMutation.isPending}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/20"
                        onClick={() => rejectMutation.mutate(candidate.id)}
                        disabled={rejectMutation.isPending}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableShell>

      {/* 编辑对话框 */}
      <Dialog open={!!editingCandidate} onOpenChange={() => setEditingCandidate(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">修改术语候选</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="edit-source" className="text-xs">源术语</Label>
              <Input
                id="edit-source"
                value={editForm.sourceText}
                onChange={(e) => setEditForm({ ...editForm, sourceText: e.target.value })}
                className="h-9"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-target" className="text-xs">目标术语</Label>
              <Input
                id="edit-target"
                value={editForm.targetText}
                onChange={(e) => setEditForm({ ...editForm, targetText: e.target.value })}
                className="h-9"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="edit-context" className="text-xs">上下文/备注</Label>
              <Input
                id="edit-context"
                value={editForm.context}
                onChange={(e) => setEditForm({ ...editForm, context: e.target.value })}
                className="h-9"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setEditingCandidate(null)}>
              取消
            </Button>
            <Button size="sm" onClick={saveEdit} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? '保存中...' : '保存并关闭'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
